<template>
  <transition name="fade" key="1">
    <div v-if="isOpen" class="modal-overlay" @click="close" role="dialog" aria-labelledby="quantity-modal-title" aria-modal="true">
      <div class="modal-window" @click.stop>
        <div class="left-col">
          <button class="close-button" @click="close" v-html="svgLib.close" aria-label="Close modal"></button>
          
          <div class="scrollable-content">
            <div class="modal-header" id="quantity-modal-title">
              <h4 class="truncated-title">{{ titleText }}</h4>
            </div>

            <div class="quantity-section">
              <div class="quantity-controls">
                <button class="ctrl-btn minus" @click="decrement" :disabled="quantity !== null && quantity <= min">−</button>
                
                <div class="quantity-input-container">
                  <input 
                    type="number" 
                    v-model.number="quantity" 
                    :min="min" 
                    :max="max"
                    class="quantity-input"
                    placeholder="0"
                    @input="validateQuantity"
                    @keyup.enter="confirm"
                  />
                </div>
                
                <button class="ctrl-btn plus" @click="increment" :disabled="quantity !== null && quantity >= max">+</button>
              </div>
            </div>

            <div class="modifier-sets">
              <div class="keypad-section">
                <div class="keypad-header">
                  <h5>Enter Quantity</h5>
                  <div class="price-pill" v-if="price">{{ formatPrice(price) }}</div>
                </div>
                <p>Tap numbers or use +/- buttons</p>
                <div class="keypad-grid">
                  <div class="keypad-item" v-for="digit in [1,2,3]" :key="digit">
                    <div class="mod-hold" @click="inputDigit(digit)">
                      <span class="digit">{{ digit }}</span>
                    </div>
                  </div>
                  <div class="keypad-item" v-for="digit in [4,5,6]" :key="digit">
                    <div class="mod-hold" @click="inputDigit(digit)">
                      <span class="digit">{{ digit }}</span>
                    </div>
                  </div>
                  <div class="keypad-item" v-for="digit in [7,8,9]" :key="digit">
                    <div class="mod-hold" @click="inputDigit(digit)">
                      <span class="digit">{{ digit }}</span>
                    </div>
                  </div>
                  <div class="keypad-item">
                    <div class="mod-hold special" @click="clear">
                      <span class="digit">Clear</span>
                    </div>
                  </div>
                  <div class="keypad-item">
                    <div class="mod-hold" @click="inputDigit(0)">
                      <span class="digit">0</span>
                    </div>
                  </div>
                  <div class="keypad-item">
                    <div class="mod-hold special" @click="backspace">
                      <span class="digit">⌫</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="item-controls">
            <button class="btn btn-lg btn-primary w-100" @click="confirm" :disabled="!isValidQuantity">
              {{ confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import svgLib from "@/utils/svgLib";

export default {
  name: 'QuantityPrompt',
  props: {
    isOpen: { type: Boolean, required: true },
    itemName: { type: String, required: false, default: '' },
    min: { type: Number, default: 1 },
    max: { type: Number, default: 99 },
    defaultQuantity: { type: Number, default: 1 },
    confirmText: { type: String, default: 'Add' },
    title: { type: String, default: null },
    price: { type: Number, required: false, default: null },
    currency: { type: String, default: '$' }
  },
  data() {
    return {
      quantity: null,
      svgLib,
    };
  },
  computed: {
    displayQuantity() {
      return this.quantity;
    },
    titleText() {
      if (this.title) return this.title;
      return this.itemName ? this.itemName : 'Enter Quantity';
    },
    confirmLabel() {
      if (this.quantity > 0 && this.price) {
        return `${this.confirmText} ${this.quantity} for ${this.formatPrice(this.totalPrice)}`;
      }
      if (this.quantity > 0) {
        return `${this.confirmText} ${this.quantity}`;
      }
      return this.confirmText;
    },
    totalPrice() {
      return this.price && this.quantity ? this.price * this.quantity : 0;
    },
    isValidQuantity() {
      return this.quantity !== null && this.quantity >= this.min && this.quantity <= this.max;
    }
  },
  watch: {
    defaultQuantity(val) {
      this.quantity = val;
    },
    isOpen(newVal) {
      if (newVal) {
        this.quantity = null;
      }
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    confirm() {
      if (this.isValidQuantity) {
        this.$emit('confirm', Math.max(this.min, Math.min(this.max, this.quantity)));
        this.close();
      }
    },
    increment() {
      if (this.quantity === null) {
        this.quantity = this.min;
      } else if (this.quantity < this.max) {
        this.quantity += 1;
      }
    },
    decrement() {
      if (this.quantity === null) {
        this.quantity = this.min;
      } else if (this.quantity > this.min) {
        this.quantity -= 1;
      }
    },
    inputDigit(digit) {
      if (this.quantity === null || this.quantity === 0) {
        this.quantity = digit;
      } else {
        const currentStr = this.quantity.toString();
        const newStr = currentStr + digit.toString();
        const newValue = parseInt(newStr);
        
        if (newValue <= this.max) {
          this.quantity = newValue;
        }
      }
    },
    clear() {
      this.quantity = null;
    },
    backspace() {
      if (this.quantity === null) return;
      
      const currentStr = this.quantity.toString();
      if (currentStr.length > 1) {
        const newStr = currentStr.slice(0, -1);
        this.quantity = parseInt(newStr);
      } else {
        this.quantity = null;
      }
    },
    validateQuantity() {
      if (this.quantity !== null) {
        if (this.quantity < this.min) this.quantity = this.min;
        if (this.quantity > this.max) this.quantity = this.max;
      }
    },
    formatPrice(price) {
      if (!price && price !== 0) return '';
      // Convert cents to dollars by dividing by 100
      const dollars = price / 100;
      return `${this.currency}${dollars.toFixed(2)}`;
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  z-index: 5001;
  backdrop-filter: blur(10px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-window {
  display: flex;
  align-items: stretch;
  width: 38rem;
  height: 90vh;
  border-radius: var(--border-radius-xl);
  background: var(--primary-white);
  box-shadow: var(--box-shadow);
}

.left-col {
  width: 38rem;
  overflow-y: auto;
  overflow-x: hidden;
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: auto;
  height: 100% !important;
  background-color: var(--primary-white);
  position: relative;
  display: flex;
  flex-direction: column;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  position: absolute;
  top: 1.5rem;
  right: 2rem;
  z-index: 10;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: var(--grey-surface);
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.modal-header {
  padding: 2rem 2.125rem 1rem 2.125rem;
  border-bottom: 0.125rem solid var(--grey-outline);
}

.modal-header h4 {
  margin: 0 0 1rem 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--primary-black);
}

.truncated-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}



.quantity-section {
  padding: 2rem 2.125rem;
}

.quantity-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.quantity-input-container {
  margin: 0 1.5rem;
  min-width: 8rem;
}

.quantity-input {
  width: 100%;
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  color: var(--primary-black);
  border: 0.125rem solid var(--grey-outline);
  border-radius: var(--border-radius-xl);
  padding: 1rem;
  background: var(--primary-white);
  transition: all 0.2s ease;
}

.quantity-input:focus {
  outline: none;
  border-color: var(--brand-color);
  box-shadow: 0 0 0 0.125rem var(--primary-color-transparent);
}

.ctrl-btn {
  width: 4rem;
  height: 4rem;
  font-size: 2rem;
  font-weight: bold;
  border-radius: var(--border-radius-xl);
  border: 0.125rem solid var(--grey-outline);
  background: var(--primary-white);
  color: var(--primary-black);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ctrl-btn:hover:not(:disabled) {
  border-color: var(--brand-color);
  background-color: var(--primary-color-transparent);
  color: var(--brand-color);
  transform: scale(1.05);
}

.ctrl-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modifier-sets {
  border-top: 0.125rem solid var(--grey-outline);
}

.keypad-section {
  padding: 2rem 2.125rem;
}

.keypad-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.keypad-section h5 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-black);
}

.price-pill {
  background: var(--brand-color);
  color: var(--primary-white);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  white-space: nowrap;
}

.keypad-section p {
  color: var(--grey-medium);
  font-weight: 100;
  font-size: 1.125rem;
  line-height: 1.5rem;
  margin-bottom: 2rem;
}

.keypad-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1rem;
}

.keypad-item {
  display: flex;
  flex-direction: column;
}

.mod-hold {
  background: var(--primary-white);
  border: 0.125rem solid var(--grey-outline);
  border-radius: var(--border-radius-xl);
  padding: 1.25rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 3.5rem;
  text-align: center;
  flex: 1;
}

.mod-hold:hover {
  border-color: var(--brand-color);
  background-color: var(--primary-color-transparent);
  color: var(--brand-color);
  transform: scale(0.98);
}

.mod-hold.special {
  background: var(--grey-surface);
  color: var(--grey-dark);
}

.mod-hold.special:hover {
  background: var(--grey-medium);
  color: var(--primary-white);
  border-color: var(--grey-medium);
}

.digit {
  font-size: 1.5rem;
  font-weight: 600;
}

.item-controls {
  flex-shrink: 0;
  width: 100%;
  padding: 1.5rem 2.125rem;
  background: var(--primary-white);
  border-top: 0.125rem solid var(--grey-outline);
  z-index: 10;
}

.btn {
  border-radius: var(--border-radius-xl);
  font-weight: 600;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.btn-lg {
  padding: 1rem 2rem;
  font-size: 1.25rem;
}

.btn-primary {
  background: var(--brand-color);
  color: var(--primary-white);
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-color-darker);
  transform: scale(0.98);
}

.btn-primary:disabled {
  background: var(--grey-outline);
  color: var(--grey-medium);
  cursor: not-allowed;
  transform: none;
}

.w-100 {
  width: 100%;
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>



